import * as React from 'react';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    getInterstitialPolicies,
    getInterstitialTriggerRegistry,
    getUserTags,
    previewInterstitialPolicy,
    rollbackInterstitialPolicies,
    updateInterstitialPolicies,
} from '../Api';
import FieldInfoButton, { FieldInfoContent, LabelWithInfo } from '../components/promo/FieldInfoButton';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import {
    INTERSTITIAL_PLACEMENTS,
    INTERSTITIAL_PLACEMENT_HINTS,
    INTERSTITIAL_PLACEMENT_LABELS,
    InterstitialPoliciesBundle,
    InterstitialPolicyDocument,
    InterstitialPolicyGlobal,
    InterstitialPolicyPlacement,
    InterstitialPolicyPreview,
    InterstitialPolicyRule,
    InterstitialPlacementName,
    defaultNewSegment,
    emptyGlobal,
    emptyPlacements,
    isBuiltInPlacement,
    recommendedInterstitialPolicies,
    slugifyPolicyId,
    uniquePolicyId,
} from '../models/dtos/InterstitialPolicyDocument';

/** Same idea as custom campaigns: do not offer these as Appodeal triggers. */
const EVENT_TRIGGER_BLOCKLIST = new Set([
    'InterstitialShown', 'InterstitialClicked', 'InterstitialClosed', 'InterstitialSkipped', 'InterstitialVideoCompleted',
    'InterstitialAdShown', 'InterstitialAdSkipped', 'InterstitialPolicyResolved',
    'AdImpression', 'AdRewardEarned',
    'BetWon', 'MiniGameWon', 'MiniGameLost', // covered by built-in UI moments
    'TagAssigned', 'TagRemoved', 'SessionStarted', 'ServerDown',
]);

interface EventTriggerRow {
    event: string;
    enabled: boolean;
    cooldownMinutes: number;
}

const HELP = {
    page: {
        title: 'What is this page?',
        what: 'This configures Appodeal interstitial ads on Android by user segment. You do NOT pick creatives here — the network fills the ad. You only decide WHO sees ads and HOW OFTEN, at a few fixed moments in the app.',
        examples: [
            'Recent purchasers → “No ads” so paying users are not annoyed.',
            'Old high-balance players who never buy → keep ads on with a short cooldown (e.g. 5 min).',
            'Everyone else → fallback segment with a calmer cooldown (e.g. 15 min).',
        ],
        tip: 'This is separate from Marketing → Custom interstitials (your HTML/video campaigns on analytics events).',
    },
    matching: {
        title: 'How a user gets a segment',
        what: 'Tagged segments are checked top to bottom. Exclusive (default) stops at the first match. Combine lets several ads-on matches merge their moments. No-ads is always exclusive. Users who match none get Everyone else.',
        examples: [
            'User has tag Android_Purchasers → matches “purchasers” → No ads (stops).',
            'Two Combine segments (after bet + app open) both match → both moments apply.',
            'User has no matching tags → Everyone else settings.',
        ],
        tip: 'Use Combine only when the same users should get moments from more than one segment.',
    },
    combine: {
        title: 'Combine with other segments',
        what: 'Off (Exclusive) = this match alone wins; any Combine matches above it are discarded and the cascade stops. On (Combine) = keep checking and merge enabled moments / stricter cooldowns with other Combine matches.',
        examples: [
            'After-bet + App-open, both Combine, user in both tags → both triggers.',
            'Combine pack above an Exclusive match → Exclusive alone (the pack is dropped).',
            'Leave Exclusive for most audiences; turn Combine on only for additive packs.',
        ],
        tip: 'Put Combine packs together. No ads is always exclusive.',
    },
    globalKill: {
        title: 'Interstitials on for the app',
        what: 'Master switch for Appodeal interstitials on new Android builds. Off = nobody in those builds sees these ads, regardless of segments.',
        examples: [
            'On during normal operation.',
            'Off temporarily if you need to stop all Appodeal interstitials quickly.',
        ],
        tip: 'Older app builds may ignore this switch and still use the fallback cooldown.',
    },
    starter: {
        title: 'Load starter segments',
        what: 'Fills the editor with a ready-made list (purchasers no-ads, day-one, parasites, eCPM tiers, default). Nothing goes live until you press Save.',
        examples: [
            'Use it as a starting point, then edit tags/cooldowns to match your real tags.',
            'If you already have saved segments, this replaces the editor content — restore previous if you clicked by mistake.',
        ],
    },
    restore: {
        title: 'Restore previous',
        what: 'Puts back the last saved document from the server (up to ~10 versions). Discards unsaved edits on screen.',
        examples: [
            'You saved a bad cooldown → Restore previous → Save again if needed.',
        ],
    },
    save: {
        title: 'Save',
        what: 'Publishes the segment list to the backend. New app builds pick it up within about an hour.',
        examples: [
            'Edit → Preview a user (optional) → Save.',
        ],
        tip: 'Preview uses the last saved config, not the draft on screen.',
    },
    segmentsList: {
        title: 'Segments list (priority)',
        what: 'Each row is one tagged audience. Order matters: first match wins. “Everyone else” is below the list and is not part of this cascade.',
        examples: [
            'Top: purchasers (No ads).',
            'Middle: parasites (5 min).',
            'Everyone else: settings for users who matched no row above.',
        ],
        tip: 'Use the drag handle on the left to reorder tagged segments only.',
    },
    addSegment: {
        title: 'Add segment',
        what: 'Creates a new segment with safe defaults: ads on, bet-won + minigame enabled, 5 min bet-won cooldown, auto technical id.',
        examples: [
            'Add segment → set include tag “parasites” → set cooldown 5 → Save.',
        ],
    },
    active: {
        title: 'Active',
        what: 'Inactive segments are ignored when matching. Use this to park a segment without deleting it.',
        examples: [
            'Turn off “day_one” during a test without losing its settings.',
        ],
    },
    who: {
        title: 'Who is in this segment',
        what: 'Define the audience with user tags from analytics. Include = must have at least one. Exclude = must have none.',
        examples: [
            'Include: Android_Purchasers → paying users.',
            'Include: parasites · Exclude: Android_Purchasers → parasites who are not purchasers.',
        ],
        tip: 'Every tagged segment needs ≥1 include tag. Users outside all segments use Everyone else.',
    },
    includeTags: {
        title: 'Include tags',
        what: 'The user must have at least one of these tags to enter the segment. Matching is case-insensitive.',
        examples: [
            'Android_Purchasers',
            'parasites',
            'NoPurch_20Bets_3Days',
        ],
    },
    excludeTags: {
        title: 'Exclude tags',
        what: 'If the user has any of these tags, they do not match this segment even if include would match.',
        examples: [
            'Segment “parasites” includes parasites but excludes Android_Purchasers.',
        ],
    },
    everyoneElse: {
        title: 'Everyone else',
        what: 'Settings for users who did not match any tagged segment above (and for holdout). Not a normal segment in the priority list.',
        examples: [
            'No ads for the long tail.',
            'Or a calmer 15 min cooldown for everyone left.',
        ],
        tip: 'You always have exactly one Everyone else configuration.',
    },
    showAds: {
        title: 'Show interstitial ads?',
        what: 'Off = this audience never gets Appodeal interstitials (“No ads”). On = they can see ads according to cooldowns and moments below.',
        examples: [
            'Off for recent purchasers you do not want to annoy.',
            'On for old IAP-tagged whales with huge balances who no longer buy or watch rewarded — you can still show interstitials to them.',
        ],
        tip: '“No ads” must sit above broader “ads on” segments, or those users may match the broader one first.',
    },
    howOften: {
        title: 'How often (bulk cooldown)',
        what: 'Sets the same cooldown (minutes) on every moment that is currently enabled for this segment. Faster than editing each row.',
        examples: [
            'Type 5 → Apply → bet-won and other enabled moments become 5 minutes.',
            'Type 15 → Apply for a calmer segment.',
            '0 = no per-moment cooldown (global min-gap in More options can still throttle).',
        ],
    },
    where: {
        title: 'Where ads can show',
        what: 'Two kinds of triggers: (1) Built-in UI moments (dialog closes) and (2) Analytics events — the same EventType list as Marketing → Custom interstitials (ShopClosed, LevelUp, …). When that event is tracked on the phone, Appodeal can show if this segment enables it.',
        examples: [
            'Built-in: Bet won dialog with cooldown 5.',
            'Event: ShopClosed with cooldown 10 → closing the shop may show an interstitial.',
            'Do not re-add BetWon / MiniGameWon as events — use the built-in rows (avoids double ads).',
        ],
        tip: 'Custom HTML/video campaigns still use Marketing → Custom interstitials (push). This page is Appodeal only.',
    },
    eventTriggers: {
        title: 'Analytics event triggers',
        what: 'Same registry as custom interstitial campaigns. Pick an EventType; when Android tracks it, the local policy may show an Appodeal interstitial for this segment.',
        examples: [
            'Add ShopClosed → enable → cooldown 10.',
            'Add LevelUp → enable → cooldown 15.',
        ],
        tip: 'The event must already be tracked in the app. New moments need an Android track() first, then they appear here.',
    },
    placementCooldown: {
        title: 'Cooldown (minutes) for a moment',
        what: 'Minimum time after this moment last showed an interstitial before it can show again for this user.',
        examples: [
            '5 = at most about once every 5 minutes from that moment.',
            '15 = quieter.',
            '0 = this moment has no own cooldown (other limits may still apply).',
        ],
    },
    everyN: {
        title: 'Every N wins (minigame)',
        what: 'Only for minigame: show an interstitial every N wins, not after every single win.',
        examples: [
            '3 = after the 3rd, 6th, 9th… win.',
            '1 = after every win (still subject to cooldown / gap).',
        ],
    },
    moreOptions: {
        title: 'More options',
        what: 'Advanced controls you usually leave alone: fallback flag, technical id, extra caps, and holdout experiment.',
        examples: [
            'Open this if you need to mark the fallback segment or tune daily caps.',
        ],
    },
    fallback: {
        title: 'Everyone else settings',
        what: 'Used for holdout users and for anyone who matched no tagged segment. Edited from the Everyone else row under the segment list.',
        examples: [
            'Turn ads off for the long tail.',
            'Or keep ads on with a longer cooldown.',
        ],
    },
    technicalId: {
        title: 'Technical id',
        what: 'Internal name stored in the config (letters, numbers, _ or -). Auto-fills from the first include tag; edit to lock a custom id.',
        examples: [
            'Tag Android_Purchasers → id android_purchasers.',
            'Custom id: no_ads_recent_iap.',
        ],
    },
    minGap: {
        title: 'Min gap (seconds)',
        what: 'Minimum seconds between any interstitial (including custom Marketing campaigns). Shared quiet period.',
        examples: [
            '60 = at least one minute between any full-screen interstitial.',
            '0 = off.',
        ],
    },
    maxSession: {
        title: 'Max per session',
        what: 'Cap on how many interstitials in one app session. Session ends after ~30 minutes in background.',
        examples: [
            '3 = at most three per session.',
            '0 = unlimited.',
        ],
    },
    maxDay: {
        title: 'Max per day',
        what: 'Cap per local calendar day on the device.',
        examples: [
            '10 = at most ten per day.',
            '0 = unlimited.',
        ],
    },
    signupGrace: {
        title: 'Signup grace (minutes)',
        what: 'No interstitials for this many minutes after account signup.',
        examples: [
            '60 = first hour after signup is ad-free for this segment.',
        ],
    },
    rewardedQuiet: {
        title: 'Quiet after rewarded',
        what: 'After a rewarded video pays out, wait this many minutes before an interstitial.',
        examples: [
            '10 = do not immediately follow a rewarded with an interstitial.',
        ],
    },
    requireFirstBet: {
        title: 'Require a real bet first',
        what: 'No interstitial until the user has placed a real bet at least once.',
        examples: [
            'On for day-one users so they place a bet before seeing ads.',
        ],
    },
    holdout: {
        title: 'Holdout experiment',
        what: 'A stable % of users always get the fallback segment (baseline), so you can compare metrics against segmented rules.',
        examples: [
            'Holdout 20% + salt “int_seg_2026_10” → ~20% always see fallback rules.',
            'Changing salt reshuffles who is in holdout — confirm before Save.',
        ],
    },
    preview: {
        title: 'Preview a user',
        what: 'Asks the server which segment the last saved config would pick for that betup id, and shows tags + a short summary.',
        examples: [
            'Enter 123456 → Preview → “Segment parasites (test): bet-won 5 min …”.',
        ],
        tip: 'Does not use unsaved edits — Save first if you want to preview your draft.',
    },
} satisfies Record<string, FieldInfoContent>;


interface EditablePolicy extends InterstitialPolicyRule {
    clientKey: string;
    /** When true, changing include tags can rewrite the technical id. */
    autoId: boolean;
    combineWithOthers: boolean;
    global: InterstitialPolicyGlobal;
    placements: Record<InterstitialPlacementName, InterstitialPolicyPlacement>;
    /** Extra analytics EventType triggers (same names as Custom interstitials). */
    eventTriggers: EventTriggerRow[];
}

interface EditorState {
    version: number;
    globalEnabled: boolean;
    experimentEnabled: boolean;
    holdoutPercent: string;
    salt: string;
    defaultPolicyId: string;
    policies: EditablePolicy[];
}

function newKey(): string {
    return `p-${Math.random().toString(36).slice(2, 10)}`;
}

function looksAutoId(id: string): boolean {
    return /^segment(_\d+)?$/i.test(id.trim()) || id.trim() === '';
}

function normalizePolicy(policy: InterstitialPolicyRule): EditablePolicy {
    const placements = emptyPlacements();
    const eventTriggers: EventTriggerRow[] = [];
    Object.entries(policy.placements ?? {}).forEach(([name, incoming]) => {
        if (!incoming) return;
        if (isBuiltInPlacement(name)) {
            placements[name] = {
                enabled: incoming.enabled === true,
                cooldownMinutes: Number(incoming.cooldownMinutes) || 0,
                ...(name === 'interstitial_minigame'
                    ? { everyNWins: Number(incoming.everyNWins) || 1 }
                    : {}),
            };
            return;
        }
        eventTriggers.push({
            event: name,
            enabled: incoming.enabled === true,
            cooldownMinutes: Number(incoming.cooldownMinutes) || 0,
        });
    });
    eventTriggers.sort((a, b) => a.event.localeCompare(b.event));
    return {
        clientKey: newKey(),
        id: policy.id,
        autoId: looksAutoId(policy.id),
        enabled: policy.enabled,
        includeTags: policy.includeTags ?? [],
        excludeTags: policy.excludeTags ?? [],
        interstitialsEnabled: policy.interstitialsEnabled,
        combineWithOthers: policy.interstitialsEnabled === false ? false : policy.combineWithOthers === true,
        global: { ...emptyGlobal(), ...(policy.global ?? {}) },
        placements,
        eventTriggers,
    };
}

function ensureLeftover(document: InterstitialPolicyDocument): InterstitialPolicyDocument {
    const defaultPolicyId = document.defaultPolicyId?.trim() || 'default';
    if (document.policies.some(policy => policy.id === defaultPolicyId)) {
        return { ...document, defaultPolicyId };
    }
    return {
        ...document,
        defaultPolicyId,
        policies: [
            ...document.policies,
            {
                id: defaultPolicyId,
                enabled: true,
                includeTags: [],
                excludeTags: [],
                interstitialsEnabled: true,
                global: emptyGlobal(),
                placements: emptyPlacements(),
            },
        ],
    };
}

function toEditor(document: InterstitialPolicyDocument): EditorState {
    const ensured = ensureLeftover(document);
    return {
        version: ensured.version,
        globalEnabled: ensured.globalEnabled,
        experimentEnabled: ensured.experiment.enabled,
        holdoutPercent: String(ensured.experiment.holdoutPercent),
        salt: ensured.experiment.salt,
        defaultPolicyId: ensured.defaultPolicyId,
        policies: ensured.policies.map(normalizePolicy),
    };
}

function audiencePolicies(state: EditorState): EditablePolicy[] {
    return state.policies.filter(policy => policy.id !== state.defaultPolicyId);
}

function leftoverPolicy(state: EditorState): EditablePolicy | undefined {
    return state.policies.find(policy => policy.id === state.defaultPolicyId);
}

function wholeNumber(raw: string): number {
    const text = raw.trim();
    if (!/^\d+$/.test(text)) return Number.NaN;
    return Number(text);
}

function toDocument(state: EditorState): InterstitialPolicyDocument | string {
    const holdout = wholeNumber(state.holdoutPercent);
    if (Number.isNaN(holdout) || holdout > 100) return 'Holdout percent must be a whole number from 0 to 100.';
    if (state.experimentEnabled && state.salt.trim() === '') return 'Experiment salt is required while the experiment is on.';
    if (!leftoverPolicy(state)) return 'Everyone else settings are missing.';
    const ids = state.policies.map(policy => policy.id.trim());
    if (ids.some(id => !/^[A-Za-z0-9_-]{1,64}$/.test(id))) {
        return 'Each segment id must use letters, numbers, _ or - (max 64).';
    }
    if (new Set(ids.map(id => id.toLowerCase())).size !== ids.length) return 'Segment ids must be unique.';
    if (!ids.includes(state.defaultPolicyId)) return 'Everyone else settings id is missing from the document.';
    for (const policy of state.policies) {
        const isLeftover = policy.id === state.defaultPolicyId;
        if (!isLeftover && policy.includeTags.every(tag => !tag.trim())) {
            return `Segment ${policy.id} needs at least one include tag. Users outside all segments use Everyone else.`;
        }
        const label = isLeftover ? 'Everyone else' : `Segment ${policy.id}`;
        const eventNames = policy.eventTriggers.map(row => row.event.trim());
        if (eventNames.some(name => !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(name))) {
            return `${label}: event names must look like analytics EventType (e.g. ShopClosed).`;
        }
        if (new Set(eventNames.map(name => name.toLowerCase())).size !== eventNames.length) {
            return `${label}: duplicate event triggers.`;
        }
        const numbers = [
            policy.global.minGapSeconds,
            policy.global.maxPerSession,
            policy.global.maxPerDay,
            policy.global.signupGraceMinutes,
            policy.global.rewardedQuietMinutes,
            ...INTERSTITIAL_PLACEMENTS.map(name => policy.placements[name].cooldownMinutes),
            ...policy.eventTriggers.map(row => row.cooldownMinutes),
        ];
        if (numbers.some(value => !Number.isInteger(value) || value < 0)) {
            return `${label} has a field that is not a whole number of 0 or more.`;
        }
        const everyN = policy.placements.interstitial_minigame.everyNWins ?? 0;
        if (policy.placements.interstitial_minigame.enabled && (!Number.isInteger(everyN) || everyN < 1)) {
            return `${label}: minigame every-N wins must be 1 or more.`;
        }
    }
    return {
        version: state.version,
        globalEnabled: state.globalEnabled,
        experiment: {
            enabled: state.experimentEnabled,
            holdoutPercent: holdout,
            salt: state.salt,
        },
        defaultPolicyId: state.defaultPolicyId,
        policies: state.policies.map(policy => {
            const isLeftover = policy.id === state.defaultPolicyId;
            return {
                id: policy.id.trim(),
                enabled: isLeftover ? true : policy.enabled,
                includeTags: isLeftover ? [] : policy.includeTags,
                excludeTags: isLeftover ? [] : policy.excludeTags,
                interstitialsEnabled: policy.interstitialsEnabled,
                combineWithOthers: !isLeftover && policy.interstitialsEnabled && policy.combineWithOthers,
                global: policy.global,
                placements: {
                    ...Object.fromEntries(
                        INTERSTITIAL_PLACEMENTS.map(name => {
                            const rule = policy.placements[name];
                            return [name, name === 'interstitial_minigame'
                                ? { enabled: rule.enabled, cooldownMinutes: rule.cooldownMinutes, everyNWins: rule.everyNWins ?? 1 }
                                : { enabled: rule.enabled, cooldownMinutes: rule.cooldownMinutes }];
                        }),
                    ),
                    ...Object.fromEntries(
                        policy.eventTriggers
                            .filter(row => row.event.trim() !== '')
                            .map(row => [row.event.trim(), {
                                enabled: row.enabled,
                                cooldownMinutes: row.cooldownMinutes,
                            }]),
                    ),
                },
            };
        }),
    };
}

function segmentSummary(policy: EditablePolicy, isLeftover = false): string {
    if (!policy.enabled && !isLeftover) return 'off';
    if (!policy.interstitialsEnabled) return 'no ads';
    const betWon = policy.placements.interstitial_betwon_dialog;
    const parts: string[] = [];
    if (policy.combineWithOthers) parts.push('combine');
    if (betWon.enabled) parts.push(`${betWon.cooldownMinutes}m bet-won`);
    if (isLeftover) parts.push('no matching segment');
    else if (policy.includeTags[0]) parts.push(policy.includeTags[0]);
    else parts.push('needs tags');
    return parts.join(' · ');
}

function SortablePolicyRow(props: {
    policy: EditablePolicy;
    selected: boolean;
    onSelect: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.policy.clientKey });
    return (
        <Stack
            ref={setNodeRef}
            direction="row"
            alignItems="center"
            gap={1}
            onClick={props.onSelect}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
            sx={{
                px: 1,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: props.selected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <Box {...attributes} {...listeners} sx={{ display: 'flex', cursor: 'grab', color: 'text.disabled' }} onClick={event => event.stopPropagation()}>
                <DragHandleIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{props.policy.id || 'untitled'}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {segmentSummary(props.policy)}
                </Typography>
            </Box>
        </Stack>
    );
}

export default function PromoInterstitialSettingsPage() {
    const [loading, setLoading] = React.useState(true);
    const [editor, setEditor] = React.useState<EditorState | null>(null);
    const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
    const [persisted, setPersisted] = React.useState(true);
    const [canRollback, setCanRollback] = React.useState(false);
    const [warnings, setWarnings] = React.useState<string[]>([]);
    const [savedSalt, setSavedSalt] = React.useState('');
    const [tags, setTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [eventRegistry, setEventRegistry] = React.useState<string[]>([]);
    const [eventToAdd, setEventToAdd] = React.useState<string | null>(null);
    const [previewId, setPreviewId] = React.useState('');
    const [preview, setPreview] = React.useState<InterstitialPolicyPreview | null>(null);
    const [bulkCooldown, setBulkCooldown] = React.useState('5');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');
    const [confirmSalt, setConfirmSalt] = React.useState(false);
    const [confirmRollback, setConfirmRollback] = React.useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const applyBundle = (bundle: InterstitialPoliciesBundle) => {
        const next = toEditor(bundle.config);
        setEditor(next);
        setSelectedKey(current => current && next.policies.some(policy => policy.clientKey === current)
            ? current
            : next.policies[0]?.clientKey ?? null);
        setPersisted(bundle.persisted);
        setCanRollback(bundle.canRollback);
        setWarnings(bundle.warnings ?? []);
        setSavedSalt(bundle.config.experiment.salt);
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const [bundle, userTags, triggers] = await Promise.all([
                getInterstitialPolicies(),
                getUserTags(),
                getInterstitialTriggerRegistry(),
            ]);
            if (cancelled) return;
            if (bundle) applyBundle(bundle);
            setTags((userTags ?? []).slice().sort((a, b) => a.tagName.localeCompare(b.tagName)));
            setEventRegistry(
                (triggers?.events ?? [])
                    .filter(name => !EVENT_TRIGGER_BLOCKLIST.has(name))
                    .slice()
                    .sort((a, b) => a.localeCompare(b)),
            );
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const updatePolicy = (clientKey: string, patch: Partial<EditablePolicy>) => {
        setEditor(current => current && ({
            ...current,
            policies: current.policies.map(policy => policy.clientKey === clientKey ? { ...policy, ...patch } : policy),
        }));
    };

    const renamePolicyId = (clientKey: string, nextId: string, autoId: boolean) => {
        setEditor(current => {
            if (!current) return current;
            const selected = current.policies.find(policy => policy.clientKey === clientKey);
            if (!selected) return current;
            return {
                ...current,
                defaultPolicyId: current.defaultPolicyId === selected.id ? nextId : current.defaultPolicyId,
                policies: current.policies.map(policy =>
                    policy.clientKey === clientKey ? { ...policy, id: nextId, autoId } : policy,
                ),
            };
        });
    };

    const setIncludeTags = (clientKey: string, includeTags: string[]) => {
        setEditor(current => {
            if (!current) return current;
            const policies = current.policies.map(policy => {
                if (policy.clientKey !== clientKey) return policy;
                if (policy.id === current.defaultPolicyId) {
                    return { ...policy, includeTags: [] };
                }
                if (!policy.autoId) {
                    return { ...policy, includeTags };
                }
                const nextId = uniquePolicyId(
                    slugifyPolicyId(includeTags[0] || 'segment') || 'segment',
                    current.policies.filter(other => other.clientKey !== clientKey).map(other => other.id),
                );
                return { ...policy, includeTags, id: nextId, autoId: true };
            });
            return { ...current, policies };
        });
    };

    const save = async () => {
        if (!editor) return;
        const document = toDocument(editor);
        if (typeof document === 'string') {
            openDialog('Check the segments', document);
            return;
        }
        if (editor.experimentEnabled && editor.salt !== savedSalt) {
            setConfirmSalt(true);
            return;
        }
        await persist(document);
    };

    const persist = async (document: InterstitialPolicyDocument) => {
        const result = await updateInterstitialPolicies(document);
        if (!result.ok) {
            openDialog('Not saved', result.error);
            return;
        }
        applyBundle(result.bundle);
        openDialog('Saved', 'New app builds pick this up within about an hour. Older builds keep using the mirrored cooldown, every-N, and excluded tags.');
    };

    const onDragEnd = (event: DragEndEvent) => {
        if (!editor || !event.over || event.active.id === event.over.id) return;
        const audiences = audiencePolicies(editor);
        const leftover = leftoverPolicy(editor);
        const from = audiences.findIndex(policy => policy.clientKey === event.active.id);
        const to = audiences.findIndex(policy => policy.clientKey === event.over?.id);
        if (from < 0 || to < 0) return;
        const nextAudiences = arrayMove(audiences, from, to);
        setEditor({
            ...editor,
            policies: leftover ? [...nextAudiences, leftover] : nextAudiences,
        });
    };

    const addSegment = () => {
        if (!editor) return;
        const created = normalizePolicy(defaultNewSegment(editor.policies.map(policy => policy.id)));
        created.autoId = true;
        const leftover = leftoverPolicy(editor);
        const audiences = audiencePolicies(editor);
        setEditor({
            ...editor,
            policies: leftover ? [...audiences, created, leftover] : [...audiences, created],
        });
        setSelectedKey(created.clientKey);
        setBulkCooldown(String(created.placements.interstitial_betwon_dialog.cooldownMinutes || 5));
    };

    const applyBulkCooldown = () => {
        if (!selected) return;
        const minutes = wholeNumber(bulkCooldown);
        if (Number.isNaN(minutes)) {
            openDialog('Cooldown', 'Enter a whole number of minutes (0 or more).');
            return;
        }
        const placements = { ...selected.placements };
        INTERSTITIAL_PLACEMENTS.forEach(name => {
            if (placements[name].enabled) {
                placements[name] = { ...placements[name], cooldownMinutes: minutes };
            }
        });
        const eventTriggers = selected.eventTriggers.map(row =>
            row.enabled ? { ...row, cooldownMinutes: minutes } : row,
        );
        updatePolicy(selected.clientKey, { placements, eventTriggers });
    };

    const selected = editor?.policies.find(policy => policy.clientKey === selectedKey) ?? null;
    const selectedIsLeftover = !!editor && !!selected && selected.id === editor.defaultPolicyId;
    const leftover = editor ? leftoverPolicy(editor) : undefined;
    const audiences = editor ? audiencePolicies(editor) : [];

    React.useEffect(() => {
        if (!selected) return;
        const enabled = INTERSTITIAL_PLACEMENTS
            .map(name => selected.placements[name])
            .filter(rule => rule.enabled);
        if (enabled.length === 0) return;
        setBulkCooldown(String(enabled[0].cooldownMinutes));
    }, [selected?.clientKey]);

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5, maxWidth: 1200 }}>
            <Stack direction="row" alignItems="flex-start" gap={1}>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="h4">Interstitial ads (Android)</Typography>
                        <FieldInfoButton info={HELP.page} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 820 }}>
                        Split users into <strong>tagged segments</strong> (cascade, first match wins).
                        Users who match none of them get separate <strong>Everyone else</strong> settings.
                        The app shows Appodeal ads only at the moments you enable.
                    </Typography>
                </Box>
            </Stack>

            <Alert severity="info">
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>How matching works</Typography>
                    <FieldInfoButton info={HELP.matching} />
                </Stack>
                <Typography variant="body2" component="div">
                    1. Tagged segments only — list order = priority. Matching is case-insensitive.<br />
                    2. <strong>Exclusive</strong> (default) = stop at first match. <strong>Combine</strong> = merge moments with other Combine matches.<br />
                    3. “No ads” is always exclusive. Put those above broader ads-on segments.<br />
                    4. <strong>Everyone else</strong> is not in the cascade — settings for users who matched no segment.<br />
                    5. Triggers = built-in UI moments and/or analytics events (same EventType list as Custom interstitials).
                </Typography>
            </Alert>

            {loading || !editor ? <CircularProgress /> : (
                <>
                    {!persisted && (
                        <Alert severity="warning">
                            Not saved yet. Live traffic still uses the previous rules until you press Save.
                        </Alert>
                    )}
                    {warnings.map(warning => <Alert key={warning} severity="warning">{warning}</Alert>)}

                    <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                        <FormControlLabel
                            control={<Switch checked={editor.globalEnabled} onChange={event => setEditor({ ...editor, globalEnabled: event.target.checked })} />}
                            label={<LabelWithInfo label="Interstitials on for the app" info={HELP.globalKill} />}
                        />
                        <Typography variant="body2" color="text.secondary">Config version {editor.version}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" alignItems="center" gap={0.25}>
                            <FieldInfoButton info={HELP.starter} />
                            <Button onClick={() => {
                                const next = toEditor(recommendedInterstitialPolicies());
                                setEditor(next);
                                setSelectedKey(next.policies[0]?.clientKey ?? null);
                            }}>
                                Load starter segments
                            </Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={0.25}>
                            <FieldInfoButton info={HELP.restore} />
                            <Button disabled={!canRollback} onClick={() => setConfirmRollback(true)}>Restore previous</Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={0.25}>
                            <FieldInfoButton info={HELP.save} />
                            <Button variant="contained" onClick={save}>Save</Button>
                        </Stack>
                    </Stack>
                    {!editor.globalEnabled && (
                        <Alert severity="warning">
                            Off = no Appodeal interstitials on new builds. Older builds ignore this switch and still use the fallback segment’s cooldown.
                        </Alert>
                    )}

                    <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems="flex-start">
                        <Box sx={{ width: { xs: '100%', md: 280 }, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1, pt: 0.5, pb: 0.5 }}>
                                <Typography variant="subtitle2">Segments (priority ↓)</Typography>
                                <FieldInfoButton info={HELP.segmentsList} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 1 }}>
                                Drag tagged segments. First match wins; Everyone else is separate.
                            </Typography>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                <SortableContext items={audiences.map(policy => policy.clientKey)} strategy={verticalListSortingStrategy}>
                                    {audiences.map(policy => (
                                        <SortablePolicyRow
                                            key={policy.clientKey}
                                            policy={policy}
                                            selected={policy.clientKey === selectedKey}
                                            onSelect={() => setSelectedKey(policy.clientKey)}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                            {leftover && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    onClick={() => setSelectedKey(leftover.clientKey)}
                                    sx={{
                                        mt: 1,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: leftover.clientKey === selectedKey ? 'action.selected' : 'transparent',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" noWrap>Everyone else</Typography>
                                            <FieldInfoButton info={HELP.everyoneElse} />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {segmentSummary(leftover, true)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            )}
                            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1 }}>
                                <FieldInfoButton info={HELP.addSegment} />
                                <Button fullWidth onClick={addSegment}>
                                    Add segment
                                </Button>
                            </Stack>
                        </Box>

                        {selected && (
                            <Stack gap={2.5} sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                                    <Typography variant="h6" sx={{ flex: 1 }}>
                                        {selectedIsLeftover ? 'Everyone else' : (selected.id || 'New segment')}
                                    </Typography>
                                    {!selectedIsLeftover && (
                                        <FormControlLabel
                                            control={<Switch checked={selected.enabled} onChange={event => updatePolicy(selected.clientKey, { enabled: event.target.checked })} />}
                                            label={<LabelWithInfo label="Active" info={HELP.active} />}
                                        />
                                    )}
                                    <Button
                                        color="error"
                                        disabled={selectedIsLeftover}
                                        onClick={() => {
                                            const remaining = editor.policies.filter(policy => policy.clientKey !== selected.clientKey);
                                            setEditor({ ...editor, policies: remaining });
                                            setSelectedKey(remaining[0]?.clientKey ?? leftover?.clientKey ?? null);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Stack>

                                {selectedIsLeftover ? (
                                    <Alert severity="info">
                                        Applies only to users who did not match any tagged segment above (and to holdout).
                                        Not part of the priority cascade.
                                    </Alert>
                                ) : (
                                    <Section
                                        title="1. Who is in this segment"
                                        hint="At least one include tag is required."
                                        info={HELP.who}
                                    >
                                        <TagPicker
                                            label="Include tags"
                                            helper="User needs at least one of these."
                                            value={selected.includeTags}
                                            options={tags}
                                            onChange={includeTags => setIncludeTags(selected.clientKey, includeTags)}
                                            info={HELP.includeTags}
                                        />
                                        <TagPicker
                                            label="Exclude tags (optional)"
                                            helper="If the user has any of these, they skip this segment."
                                            value={selected.excludeTags}
                                            options={tags}
                                            onChange={excludeTags => updatePolicy(selected.clientKey, { excludeTags })}
                                            info={HELP.excludeTags}
                                        />
                                    </Section>
                                )}

                                <Section
                                    title={selectedIsLeftover ? '1. Show interstitial ads?' : '2. Show interstitial ads?'}
                                    hint="Off = No ads for this audience. On = ads with the cooldowns you set below."
                                    info={HELP.showAds}
                                >
                                    <FormControlLabel
                                        control={<Switch checked={selected.interstitialsEnabled} onChange={event => updatePolicy(selected.clientKey, {
                                            interstitialsEnabled: event.target.checked,
                                            combineWithOthers: event.target.checked ? selected.combineWithOthers : false,
                                        })} />}
                                        label={selected.interstitialsEnabled
                                            ? (selectedIsLeftover ? 'Show ads for everyone else' : 'Show ads for this segment')
                                            : (selectedIsLeftover ? 'No ads for everyone else' : 'No ads for this segment')}
                                    />
                                </Section>

                                {!selectedIsLeftover && selected.interstitialsEnabled && (
                                    <Section
                                        title="3. Combine with other segments?"
                                        hint="Off = Exclusive (this match alone). On = merge moments with other Combine matches below/above."
                                        info={HELP.combine}
                                    >
                                        <FormControlLabel
                                            control={<Switch checked={selected.combineWithOthers} onChange={event => updatePolicy(selected.clientKey, { combineWithOthers: event.target.checked })} />}
                                            label={selected.combineWithOthers
                                                ? 'Combine — merge moments with other Combine segments this user matches'
                                                : 'Exclusive — stop here (default)'}
                                        />
                                    </Section>
                                )}

                                {selected.interstitialsEnabled && (
                                    <>
                                        <Section
                                            title={selectedIsLeftover ? '2. How often' : '4. How often'}
                                            hint="Quick way to set the same cooldown on every moment that is turned on (e.g. 5 or 15 minutes)."
                                            info={HELP.howOften}
                                        >
                                            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
                                                <TextField
                                                    label="Cooldown for all enabled moments (minutes)"
                                                    value={bulkCooldown}
                                                    onChange={event => setBulkCooldown(event.target.value)}
                                                    size="small"
                                                    sx={{ width: 280 }}
                                                    helperText="0 = no per-moment cooldown (global min-gap below can still apply)."
                                                />
                                                <Button variant="outlined" onClick={applyBulkCooldown} sx={{ mt: 0.5 }}>
                                                    Apply to enabled moments
                                                </Button>
                                            </Stack>
                                        </Section>

                                        {/* 4. Where */}
                                        <Section
                                            title={selectedIsLeftover ? '3. Where ads can show' : '5. Where ads can show'}
                                            hint="Built-in dialog moments + optional analytics events (same list as Custom interstitials)."
                                            info={HELP.where}
                                        >
                                            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Built-in UI moments</Typography>
                                            {INTERSTITIAL_PLACEMENTS.map(name => {
                                                const rule = selected.placements[name];
                                                const placementInfo: FieldInfoContent = {
                                                    title: INTERSTITIAL_PLACEMENT_LABELS[name],
                                                    what: INTERSTITIAL_PLACEMENT_HINTS[name],
                                                    examples: name === 'interstitial_minigame'
                                                        ? ['Enable + every 3 wins + cooldown 0 → interstitial about every 3rd win.', 'Cooldown 5 still limits how often even if N is 1.']
                                                        : ['Enable + cooldown 5 → at most about once every 5 minutes from this moment.', 'Leave off if you do not want ads at this moment for this segment.'],
                                                };
                                                return (
                                                    <Box key={name} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                                                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                                                            <FormControlLabel
                                                                sx={{ minWidth: 240 }}
                                                                control={<Switch checked={rule.enabled} onChange={event => updatePolicy(selected.clientKey, { placements: { ...selected.placements, [name]: { ...rule, enabled: event.target.checked } } })} />}
                                                                label={<LabelWithInfo label={INTERSTITIAL_PLACEMENT_LABELS[name]} info={placementInfo} />}
                                                            />
                                                            <NumberField
                                                                label="Cooldown (min)"
                                                                value={rule.cooldownMinutes}
                                                                onChange={cooldownMinutes => updatePolicy(selected.clientKey, { placements: { ...selected.placements, [name]: { ...rule, cooldownMinutes } } })}
                                                                info={HELP.placementCooldown}
                                                            />
                                                            {name === 'interstitial_minigame' && (
                                                                <NumberField
                                                                    label="Every N wins"
                                                                    value={rule.everyNWins ?? 1}
                                                                    onChange={everyNWins => updatePolicy(selected.clientKey, { placements: { ...selected.placements, [name]: { ...rule, everyNWins } } })}
                                                                    info={HELP.everyN}
                                                                />
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}

                                            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2">Analytics event triggers</Typography>
                                                <FieldInfoButton info={HELP.eventTriggers} />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Same EventType names as Marketing → Custom interstitials. When the app tracks the event, Appodeal may show for this segment.
                                            </Typography>
                                            {selected.eventTriggers.map((row, index) => (
                                                <Stack key={row.event} direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ py: 0.5 }}>
                                                    <FormControlLabel
                                                        sx={{ minWidth: 200 }}
                                                        control={<Switch checked={row.enabled} onChange={event => {
                                                            const eventTriggers = selected.eventTriggers.map((item, i) =>
                                                                i === index ? { ...item, enabled: event.target.checked } : item,
                                                            );
                                                            updatePolicy(selected.clientKey, { eventTriggers });
                                                        }} />}
                                                        label={row.event}
                                                    />
                                                    <NumberField
                                                        label="Cooldown (min)"
                                                        value={row.cooldownMinutes}
                                                        onChange={cooldownMinutes => {
                                                            const eventTriggers = selected.eventTriggers.map((item, i) =>
                                                                i === index ? { ...item, cooldownMinutes } : item,
                                                            );
                                                            updatePolicy(selected.clientKey, { eventTriggers });
                                                        }}
                                                        info={HELP.placementCooldown}
                                                    />
                                                    <Button
                                                        color="error"
                                                        size="small"
                                                        onClick={() => updatePolicy(selected.clientKey, {
                                                            eventTriggers: selected.eventTriggers.filter((_, i) => i !== index),
                                                        })}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            ))}
                                            <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
                                                <Autocomplete
                                                    sx={{ minWidth: 280, flex: 1 }}
                                                    options={eventRegistry.filter(name =>
                                                        !selected.eventTriggers.some(row => row.event === name),
                                                    )}
                                                    value={eventToAdd}
                                                    onChange={(_, value) => setEventToAdd(value)}
                                                    renderInput={params => (
                                                        <TextField {...params} label="Add analytics event" size="small" helperText="From the same trigger registry as custom campaigns" />
                                                    )}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    sx={{ mt: 0.5 }}
                                                    disabled={!eventToAdd}
                                                    onClick={() => {
                                                        if (!eventToAdd) return;
                                                        updatePolicy(selected.clientKey, {
                                                            eventTriggers: [
                                                                ...selected.eventTriggers,
                                                                { event: eventToAdd, enabled: true, cooldownMinutes: Number(bulkCooldown) || 5 },
                                                            ].sort((a, b) => a.event.localeCompare(b.event)),
                                                        });
                                                        setEventToAdd(null);
                                                    }}
                                                >
                                                    Add event
                                                </Button>
                                            </Stack>
                                        </Section>
                                    </>
                                )}

                                {/* Advanced */}
                                <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ width: '100%', pr: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2">More options</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Technical id, caps, holdout — usually leave defaults.
                                                </Typography>
                                            </Box>
                                            <Box onClick={event => event.stopPropagation()}>
                                                <FieldInfoButton info={HELP.moreOptions} />
                                            </Box>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack gap={2}>
                                            <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
                                                <TextField
                                                    label="Technical id"
                                                    value={selected.id}
                                                    onChange={event => renamePolicyId(selected.clientKey, event.target.value, false)}
                                                    size="small"
                                                    disabled={selectedIsLeftover}
                                                    helperText={selectedIsLeftover
                                                        ? 'Fixed id for Everyone else settings (also used by holdout).'
                                                        : selected.autoId
                                                            ? 'Auto from the first include tag. Edit to lock a custom id.'
                                                            : 'Custom id (locked).'}
                                                    sx={{ minWidth: 240 }}
                                                    InputProps={{
                                                        endAdornment: <FieldInfoButton info={HELP.technicalId} />,
                                                    }}
                                                />
                                                {!selectedIsLeftover && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            const base = selected.includeTags[0] || 'segment';
                                                            const nextId = uniquePolicyId(
                                                                slugifyPolicyId(base) || 'segment',
                                                                editor.policies.filter(p => p.clientKey !== selected.clientKey).map(p => p.id),
                                                            );
                                                            renamePolicyId(selected.clientKey, nextId, true);
                                                        }}
                                                    >
                                                        Reset auto id
                                                    </Button>
                                                )}
                                            </Stack>

                                            {selected.interstitialsEnabled && (
                                                <>
                                                    <Divider />
                                                    <Typography variant="subtitle2">Extra frequency limits</Typography>
                                                    <Stack direction="row" gap={2} flexWrap="wrap">
                                                        <NumberField label="Min gap (seconds)" value={selected.global.minGapSeconds} onChange={minGapSeconds => updatePolicy(selected.clientKey, { global: { ...selected.global, minGapSeconds } })} helper="0 = off." info={HELP.minGap} />
                                                        <NumberField label="Max per session" value={selected.global.maxPerSession} onChange={maxPerSession => updatePolicy(selected.clientKey, { global: { ...selected.global, maxPerSession } })} helper="0 = unlimited." info={HELP.maxSession} />
                                                        <NumberField label="Max per day" value={selected.global.maxPerDay} onChange={maxPerDay => updatePolicy(selected.clientKey, { global: { ...selected.global, maxPerDay } })} helper="0 = unlimited." info={HELP.maxDay} />
                                                        <NumberField label="Signup grace (minutes)" value={selected.global.signupGraceMinutes} onChange={signupGraceMinutes => updatePolicy(selected.clientKey, { global: { ...selected.global, signupGraceMinutes } })} info={HELP.signupGrace} />
                                                        <NumberField label="Quiet after rewarded (min)" value={selected.global.rewardedQuietMinutes} onChange={rewardedQuietMinutes => updatePolicy(selected.clientKey, { global: { ...selected.global, rewardedQuietMinutes } })} info={HELP.rewardedQuiet} />
                                                    </Stack>
                                                    <FormControlLabel
                                                        control={<Switch checked={selected.global.requireFirstBet} onChange={event => updatePolicy(selected.clientKey, { global: { ...selected.global, requireFirstBet: event.target.checked } })} />}
                                                        label={<LabelWithInfo label="Require a real bet before the first interstitial" info={HELP.requireFirstBet} />}
                                                    />
                                                </>
                                            )}

                                            <Divider />
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <Typography variant="subtitle2">Holdout experiment</Typography>
                                                <FieldInfoButton info={HELP.holdout} />
                                            </Stack>
                                            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                                                <FormControlLabel
                                                    control={<Switch checked={editor.experimentEnabled} onChange={event => setEditor({ ...editor, experimentEnabled: event.target.checked })} />}
                                                    label="Holdout on"
                                                />
                                                <TextField
                                                    label="Holdout %"
                                                    value={editor.holdoutPercent}
                                                    onChange={event => setEditor({ ...editor, holdoutPercent: event.target.value })}
                                                    size="small"
                                                    sx={{ width: 120 }}
                                                />
                                                <TextField
                                                    label="Salt"
                                                    value={editor.salt}
                                                    onChange={event => setEditor({ ...editor, salt: event.target.value })}
                                                    size="small"
                                                    sx={{ minWidth: 240 }}
                                                    helperText="Changing salt reshuffles who is held out."
                                                />
                                            </Stack>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </Stack>
                        )}
                    </Stack>

                    <Divider />
                    <Section title="Preview a user" hint="Uses the last saved config, not unsaved edits on this screen." info={HELP.preview}>
                        <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
                            <TextField
                                label="Betup user id"
                                value={previewId}
                                onChange={event => setPreviewId(event.target.value)}
                                size="small"
                            />
                            <Button
                                onClick={async () => {
                                    const id = wholeNumber(previewId);
                                    if (Number.isNaN(id)) {
                                        openDialog('Preview', 'Enter a numeric user id.');
                                        return;
                                    }
                                    const result = await previewInterstitialPolicy(id);
                                    if (!result) {
                                        openDialog('Preview', 'Could not resolve a policy for that user.');
                                        return;
                                    }
                                    setPreview(result);
                                }}
                            >
                                Preview saved segment
                            </Button>
                        </Stack>
                        {preview && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                User {preview.betupId} {preview.found ? `tags: ${preview.tags.join(', ') || 'none'}` : 'is not in analytics yet'}.
                                {' '}Segment <strong>{preview.policyId}</strong> ({preview.group}): {preview.summary}.
                            </Alert>
                        )}
                    </Section>
                </>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography sx={{ whiteSpace: 'pre-wrap' }}>{dialogMsg}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setDialogOpen(false)}>OK</Button></DialogActions>
            </Dialog>
            <Dialog open={confirmSalt} onClose={() => setConfirmSalt(false)}>
                <DialogTitle>Change the holdout salt?</DialogTitle>
                <DialogContent>
                    <Typography>A new salt puts a different set of users into the holdout. Users can flip between baseline and test.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSalt(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        setConfirmSalt(false);
                        if (!editor) return;
                        const document = toDocument(editor);
                        if (typeof document === 'string') {
                            openDialog('Check the segments', document);
                            return;
                        }
                        await persist(document);
                    }}>Save anyway</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmRollback} onClose={() => setConfirmRollback(false)}>
                <DialogTitle>Restore the previous save?</DialogTitle>
                <DialogContent><Typography>This puts back the last saved document. The one on screen is discarded. You can restore again to go further back, up to 10 saves.</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRollback(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        setConfirmRollback(false);
                        const result = await rollbackInterstitialPolicies();
                        if (!result.ok) {
                            openDialog('Not restored', result.error);
                            return;
                        }
                        applyBundle(result.bundle);
                        openDialog('Restored', 'The previous policy document is live again.');
                    }}>Restore</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

function Section(props: { title: string; hint: string; info?: FieldInfoContent; children: React.ReactNode }) {
    return (
        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="subtitle1">{props.title}</Typography>
                {props.info && <FieldInfoButton info={props.info} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{props.hint}</Typography>
            <Stack gap={1.5}>{props.children}</Stack>
        </Box>
    );
}

function NumberField(props: {
    label: string;
    value: number;
    helper?: string;
    info?: FieldInfoContent;
    onChange: (value: number) => void;
}) {
    return (
        <TextField
            label={props.label}
            value={String(props.value)}
            helperText={props.helper}
            size="small"
            onChange={event => {
                const parsed = wholeNumber(event.target.value);
                if (!Number.isNaN(parsed)) props.onChange(parsed);
                if (event.target.value === '') props.onChange(0);
            }}
            sx={{ width: 200 }}
            InputProps={props.info ? {
                endAdornment: <FieldInfoButton info={props.info} />,
            } : undefined}
        />
    );
}

function TagPicker(props: {
    label: string;
    helper: string;
    value: string[];
    options: UserTagDefinitionDto[];
    onChange: (tags: string[]) => void;
    info?: FieldInfoContent;
}) {
    return (
        <Autocomplete
            multiple
            freeSolo
            options={props.options.map(tag => tag.tagName)}
            value={props.value}
            onChange={(_, value) => props.onChange(value.map(tag => tag.trim()).filter(Boolean))}
            renderTags={(value, getTagProps) => value.map((tag, index) => {
                const known = props.options.find(option => option.tagName.toLowerCase() === tag.toLowerCase());
                const count = known?.approximateUsersCount;
                return <Chip {...getTagProps({ index })} key={tag} label={count == null ? tag : `${tag} (~${count})`} />;
            })}
            renderInput={params => (
                <TextField
                    {...params}
                    label={props.label}
                    helperText={props.helper}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {props.info && <FieldInfoButton info={props.info} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
